import React, { useContext, useEffect, useState } from "react";
import { Button, Input, Select, DatePicker, Modal, Upload, message } from "antd";
import { EditOutlined } from "@ant-design/icons";
import { AiOutlineEye, AiOutlineUpload } from "react-icons/ai";
import formatIndianNumber from "../../../../../utils/formatIndianNumber";
import { isGujaratBilling } from "../../../../../utils/billingGstUtils";
import convertS3ToCloudFront, {
  openFileInNewTab,
} from "../../../../../utils/convertS3ToCloudFront";
import mainContext from "../context/MainContext/mainContext";
import dayjs from "dayjs"; // Import dayjs for date handling

const { Option } = Select;
const { Dragger } = Upload;
const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

const DEFAULT_INVOICE_REMARK =
  "Being the charges for Advertisement through display your ad on Outdoor Hoarding sites at";

/** Trim + uppercase for brand/campaign; empty → null for stored/API values. */
function trimUpperOrNull(v) {
  if (v == null) return null;
  const s = String(v).trim().toUpperCase();
  return s === "" ? null : s;
}

const BillingForm = ({ billingData, handleFormChange, log }) => {
  let {
    justView,
    isArchivedbillingInvoices,
    fetchArchivedInvoiceDataByID,
    archivedInvoiceData,
    nextBillNumber,
    fetchNextBillNumber,
  } = useContext(mainContext);

  // console.log("Billing data: ", billingData);
  // console.log("Archived Invoice Data: ", archivedInvoiceData);

  const allInventoriesHaveHO =
    billingData?.inventories?.length > 0 &&
    billingData.inventories.every((inv) => inv.suffix === "HO");

  const [brandEdit, setBrandEdit] = useState(false);
  const [brandDraft, setBrandDraft] = useState("");
  const [campaignEdit, setCampaignEdit] = useState(false);
  const [campaignDraft, setCampaignDraft] = useState("");
  const [roModalOpen, setRoModalOpen] = useState(false);
  const [roUploading, setRoUploading] = useState(false);

  const canEditBrandCampaign = Boolean(billingData?.billingId) && !justView;
  const canEditRoFile = Boolean(billingData?.billingId) && !justView;

  const startBrandEdit = () => {
    setBrandDraft(
      billingData?.brandName != null && billingData.brandName !== ""
        ? String(billingData.brandName).trim().toUpperCase()
        : ""
    );
    setBrandEdit(true);
  };

  const startCampaignEdit = () => {
    setCampaignDraft(
      billingData?.campaignName != null && billingData.campaignName !== ""
        ? String(billingData.campaignName).trim().toUpperCase()
        : ""
    );
    setCampaignEdit(true);
  };

  const handleSaveBrand = () => {
    if (justView) return;
    const next = trimUpperOrNull(brandDraft);
    const prev = trimUpperOrNull(billingData?.brandName);
    if (next === prev) {
      setBrandEdit(false);
      return;
    }
    handleFormChange("brandName", next);
    setBrandEdit(false);
  };

  const handleSaveCampaign = () => {
    if (justView) return;
    const next = trimUpperOrNull(campaignDraft);
    const prev = trimUpperOrNull(billingData?.campaignName);
    if (next === prev) {
      setCampaignEdit(false);
      return;
    }
    handleFormChange("campaignName", next);
    setCampaignEdit(false);
  };

  const updateROFileFromBilling = async (roFileUrl) => {
    const billingId = billingData?.billingId;
    if (!billingId) {
      message.error("Billing ID not found");
      return false;
    }
    try {
      const response = await fetch(
        `${apiBaseUrl}/inventoryManagement/updateROFileFromBilling`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ billingId, roFile: roFileUrl }),
        },
      );
      const data = await response.json();
      if (response.ok && data?.success) {
        message.success(data?.message || "RO file updated");
        handleFormChange("roFile", roFileUrl);
        return true;
      }
      message.error(data?.message || "Failed to update RO file");
      return false;
    } catch (err) {
      message.error(err?.message || "Failed to update RO file");
      return false;
    }
  };

  const handleUploadRo = async ({ file, onSuccess, onError }) => {
    try {
      setRoUploading(true);
      const response = await fetch(`${apiBaseUrl}/inventoryManagement/uploadPolicy`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          mime: file.type,
          fileName: file.name,
        }),
      });

      const { data: policyData, filePath } = await response.json();
      const uploadUrl = convertS3ToCloudFront(`${policyData.url}/${filePath}`);

      const formData = new FormData();
      Object.entries(policyData.fields).forEach(([key, value]) => {
        if (key.toLowerCase() === "content-type") {
          formData.append(key, file.type || "application/octet-stream");
          return;
        }
        formData.append(key, value);
      });
      formData.append("file", file);

      const s3Response = await fetch(policyData.url, {
        method: "POST",
        body: formData,
      });

      if (!s3Response.ok) {
        throw new Error("S3 upload failed");
      }

      // Persist URL in billing record
      const ok = await updateROFileFromBilling(uploadUrl);
      if (!ok) {
        throw new Error("Failed to save RO file in billing");
      }

      onSuccess("Ok");
      setRoModalOpen(false);
    } catch (error) {
      console.error(error);
      onError?.(error);
      message.error(error?.message || `Failed to upload ${file?.name || "file"}`);
    } finally {
      setRoUploading(false);
    }
  };

  useEffect(() => {
    if (!billingData?.billNumber) {
      fetchNextBillNumber();
    }
  }, [billingData?.billNumber, fetchNextBillNumber]);

  useEffect(() => {
    if (
      isGujaratBilling(billingData) &&
      Number(billingData?.taxType) !== 18
    ) {
      handleFormChange("taxType", 18);
    }
  }, [
    billingData?.gstNumber,
    billingData?.splitGSTNumber,
    billingData?.state,
    billingData?.splitState,
  ]);
  const sacOptions = [
    { code: "998361", label: "Advertising Services" },
    {
      code: "998362",
      label: "Purchase or sale of advertising space or time, on commission",
    },
    {
      code: "998363",
      label: "Sale of advertising space in print media (except on commission)",
    },
    { code: "998364", label: "Sale of TV and radio advertising time" },
    { code: "998365", label: "Sale of Internet advertising space" },
    {
      code: "998366",
      label: "Sale of other advertising space or time (except on commission)",
    },
  ];

  // Convert string date to dayjs object if it exists
  const refDateValue = billingData?.refDate ? dayjs(billingData.refDate) : null;

  return (
    <div className="sales-order-form">
      {!billingData?.isFOC && (
        <div className="sales-order-field">
          <div className="sales-order-field-name">Bill Number</div>
          <div className="sales-order-input-parent">
            <Input
              className="custom-input-bdf"
              value={billingData?.billNumber ?? nextBillNumber}
              readOnly
              disabled
              style={{
                backgroundColor: "#f5f5f5",
                color: "rgba(0, 0, 0, 0.45)",
                cursor: "not-allowed",
              }}
            />
          </div>
        </div>
      )}
      <div className="sales-order-field">
        <div className="sales-order-field-name">Billing Date</div>
        <div className="sales-order-input-parent">
          <DatePicker
            className="custom-input-bdf"
            value={billingData?.billingDate ? dayjs(billingData.billingDate) : null}
            onChange={(date) => handleFormChange("billingDate", date)}
            disabled={justView}
            format="DD-MM-YYYY"
            allowClear
            placeholder="DD-MM-YYYY"
          />
        </div>
      </div>
      {!billingData?.isConsolidated && (
      <div className="sales-order-field">
        <div className="sales-order-field-name">Sales Order ID</div>
        <div className="sales-order-input-parent">
          <Input
            className="custom-input-bdf"
            value={billingData?.stamp}
            // onChange={(e) => handleFormChange("stamp", e.target.value)}
            readOnly
          />
        </div>
      </div>
      )}

      {/* 
      <div className="sales-order-field">
        <div className="sales-order-field-name">R.O. No.</div>
        <div className="sales-order-input-parent">
          <Input
            className="custom-input-bdf"
            value={billingData?.roNumber}
            onChange={(e) => handleFormChange("roNumber", e.target.value)}
            readOnly={justView}
          />
        </div>
      </div> */}

      <div className="sales-order-field">
        <div className="sales-order-field-name">Party Name</div>
        <div className="sales-order-input-parent">
          <Input
            className="custom-input-bdf"
            value={
              billingData?.splitParentCompanyName ||
              billingData?.parentCompanyName
            }
            onChange={(e) =>
              handleFormChange("parentCompanyName", e.target.value)
            }
            readOnly={justView}
            style={{
              backgroundColor:
                log &&
                  !billingData?.splitParentCompanyName &&
                  billingData?.parentCompanyName !== log?.parentCompanyName
                  ? "#fff0f0"
                  : "transparent",
            }}
          />
        </div>
      </div>

      {!billingData?.isConsolidated && (
      <div className="sales-order-field">
        <div className="sales-order-field-name">BDF Remark</div>
        <div className="sales-order-input-parent">
          <Input
            className="custom-input-bdf"
            value={billingData?.remark}
            onChange={(e) => handleFormChange("remark", e.target.value)}
            readOnly={true}
            style={{
              backgroundColor:
                log && billingData?.remark !== log?.remark
                  ? "#fff0f0"
                  : "transparent",
            }}
          />
        </div>
      </div>
      )}

      <div className="sales-order-field">
        <div className="sales-order-field-name">GST No.</div>
        <div className="sales-order-input-parent">
          <Input
            className="custom-input-bdf"
            value={billingData?.splitGSTNumber || billingData?.gstNumber}
            onChange={(e) => handleFormChange("gstNumber", e.target.value)}
            readOnly={true}
            style={{
              backgroundColor:
                log &&
                  !billingData?.splitGSTNumber &&
                  billingData?.gstNumber !== log?.gstNumber
                  ? "#fff0f0"
                  : "transparent",
            }}
          />
        </div>
      </div>

      <div className="sales-order-field">
        <div className="sales-order-field-name">Brand Name</div>
        <div
          className="sales-order-input-parent"
          style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}
        >
          <Input
            className="custom-input-bdf"
            value={
              brandEdit
                ? brandDraft
                : billingData?.brandName
                  ? String(billingData.brandName).toUpperCase()
                  : ""
            }
            onChange={(e) => {
              if (brandEdit) setBrandDraft(e.target.value.toUpperCase());
            }}
            readOnly={!brandEdit || justView}
            placeholder="Brand name"
            onPressEnter={() => {
              if (brandEdit && !justView) handleSaveBrand();
            }}
            style={{
              flex: 1,
              minWidth: 160,
              backgroundColor:
                !brandEdit &&
                log &&
                billingData?.brandName !== log?.brandName
                  ? "#fff0f0"
                  : "transparent",
            }}
          />
          {!justView && canEditBrandCampaign && !brandEdit && (
            <Button
              type="text"
              icon={<EditOutlined />}
              aria-label="Edit brand name"
              onClick={startBrandEdit}
            />
          )}
          {!justView && brandEdit && (
            <>
              <Button type="primary" size="small" onClick={handleSaveBrand}>
                Save
              </Button>
              <Button size="small" onClick={() => setBrandEdit(false)}>
                Cancel
              </Button>
            </>
          )}
        </div>
      </div>

      {/* <div className="sales-order-field">
        <div className="sales-order-field-name">Tax Type</div>
        <div className="sales-order-input-parent">
          <Select
            className="custom-select-bdf"
            value={Number(billingData?.taxType)}
            defaultValue={0}
            onChange={(value) => handleFormChange("taxType", value)}
            disabled={justView}
          >
            <Option value={0}>GST 0%</Option>
            <Option value={5}>GST 5%</Option>
            <Option value={18}>GST 18%</Option>
            <Option value={28}>GST 28%</Option>
          </Select>
        </div>
      </div> */}

      {/* <div className="sales-order-field">
        <div className="sales-order-field-name">Agency Commission (%)</div>
        <div className="sales-order-input-parent">
          <Input
            className="custom-input-bdf"
            value={billingData?.agencyCommission}
            onChange={(e) =>
              handleFormChange("agencyCommission", e.target.value)
            }
            readOnly={justView}
          />
        </div>
      </div> */}

      <div className="sales-order-field">
        <div className="sales-order-field-name">Campaign Name</div>
        <div
          className="sales-order-input-parent"
          style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}
        >
          <Input
            className="custom-input-bdf"
            value={
              campaignEdit
                ? campaignDraft
                : billingData?.campaignName
                  ? String(billingData.campaignName).toUpperCase()
                  : ""
            }
            onChange={(e) => {
              if (campaignEdit) setCampaignDraft(e.target.value.toUpperCase());
            }}
            readOnly={!campaignEdit || justView}
            placeholder="Campaign name"
            onPressEnter={() => {
              if (campaignEdit && !justView) handleSaveCampaign();
            }}
            style={{
              flex: 1,
              minWidth: 160,
              backgroundColor:
                !campaignEdit &&
                log &&
                String(billingData?.campaignName || "").toLowerCase() !==
                  String(log?.campaignName || "").toLowerCase()
                  ? "#fff0f0"
                  : "transparent",
            }}
          />
          {!justView && canEditBrandCampaign && !campaignEdit && (
            <Button
              type="text"
              icon={<EditOutlined />}
              aria-label="Edit campaign name"
              onClick={startCampaignEdit}
            />
          )}
          {!justView && campaignEdit && (
            <>
              <Button type="primary" size="small" onClick={handleSaveCampaign}>
                Save
              </Button>
              <Button size="small" onClick={() => setCampaignEdit(false)}>
                Cancel
              </Button>
            </>
          )}
        </div>
      </div>

      {!billingData?.isConsolidated && (
      <div className="sales-order-field">
        <div className="sales-order-field-name">Campaign Duration (Days)</div>
        <div className="sales-order-input-parent">
          <Input
            className="custom-input-bdf"
            value={billingData?.campaignDuration}
            onChange={(e) =>
              handleFormChange("campaignDuration", e.target.value)
            }
            readOnly={true}
            style={{
              backgroundColor:
                log && billingData?.campaignDuration !== log?.campaignDuration
                  ? "#fff0f0"
                  : "transparent",
            }}
          />
        </div>
      </div>
      )}

      {/* {allInventoriesHaveHO && (
        <div className="sales-order-field">
          <div className="sales-order-field-name">Suffix</div>
          <div className="sales-order-input-parent">
            <Input className="custom-input-bdf" value="HO" readOnly />
          </div>
        </div>
      )} */}

      {!billingData?.isConsolidated && (
      <div className="sales-order-field">
        <div className="sales-order-field-name">Sales Person Name</div>
        <div className="sales-order-input-parent">
          <Input
            className="custom-input-bdf"
            value={billingData?.salesPerson}
            readOnly
          />
        </div>
      </div>
      )}
      {!billingData?.isConsolidated && (
      <div className="sales-order-field">
        <div className="sales-order-field-name">BDF Serial No.</div>
        <div className="sales-order-input-parent">
          <Input
            className="custom-input-bdf"
            value={billingData?.bdfStamp}
            readOnly
          />
        </div>
      </div>
      )}

      <div className="sales-order-field">
        <div className="sales-order-field-name">Alias Party Name</div>
        <div className="sales-order-input-parent">
          <Input
            className="custom-input-bdf"
            value={
              archivedInvoiceData?.aliasPartyName || billingData?.aliasPartyName
            }
            onChange={(e) => handleFormChange("aliasPartyName", e.target.value)}
          // readOnly={justView}
          />
        </div>
      </div>
      <div className="sales-order-field">
        <div className="sales-order-field-name">HSN Code</div>
        <div className="sales-order-input-parent">
          <Select
            className="custom-select-bdf"
            value={archivedInvoiceData?.HSN || billingData?.HSN || sacOptions[0]?.code}
            onChange={(value) => handleFormChange("HSN", value)}
            disabled={justView}
            placeholder="Select SAC Code"
            showSearch
            optionFilterProp="children"
          >
            {sacOptions.map((item) => (
              <Option key={item.code} value={item.code}>
                {item.code} - {item.label}
              </Option>
            ))}
          </Select>
        </div>
      </div>

      {/* New Ref Number Field */}
      <div className="sales-order-field">
        <div className="sales-order-field-name">Ref Number</div>
        <div className="sales-order-input-parent">
          <Input
            className="custom-input-bdf"
            value={billingData?.refNo}
            onChange={(e) => handleFormChange("refNo", e.target.value)}
            readOnly={justView}
          />
        </div>
      </div>

      {/* New Ref Date Field */}
      <div className="sales-order-field">
        <div className="sales-order-field-name">Ref Date</div>
        <div className="sales-order-input-parent">
          <DatePicker
            className="custom-input-bdf"
            value={refDateValue}
            onChange={(date) => handleFormChange("refDate", date)}
            disabled={justView}
            format="DD-MM-YYYY"
          />
        </div>
      </div>

      {!billingData?.isConsolidated && (
      <div className="sales-order-field">
        <div className="sales-order-field-name">RO File</div>
        <div className="sales-order-input-parent">
          <Button
            type={billingData?.roFile ? "default" : "primary"}
            style={{
              borderColor: billingData?.roFile ? "#52c41a" : undefined,
              color: billingData?.roFile ? "#52c41a" : undefined,
            }}
            disabled={!billingData?.roFile && !canEditRoFile}
            onClick={() => setRoModalOpen(true)}
          >
            {billingData?.roFile ? "RO File Available • View" : "Upload RO"}
          </Button>

          <Modal
            title="RO"
            open={roModalOpen}
            onCancel={() => {
              if (roUploading) return;
              setRoModalOpen(false);
            }}
            footer={null}
            width={600}
          >
            <div style={{ width: "100%", display: "grid", placeItems: "center" }}>
              {!billingData?.roFile ? (
                <Dragger
                  customRequest={handleUploadRo}
                  style={{ width: "100%" }}
                  disabled={roUploading || !canEditRoFile}
                >
                  <p className="ant-upload-text">
                    Click or drag file to this area to upload
                  </p>
                  <p className="ant-upload-hint">Supports single file upload</p>
                </Dragger>
              ) : (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "1rem",
                  }}
                >
                  <Button
                    type="default"
                    icon={<AiOutlineEye style={{ marginRight: 2 }} />}
                    style={{
                      margin: "1rem",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                    onClick={() => {
                      if (!billingData?.roFile) {
                        message.error("RO file not found");
                        return;
                      }
                      openFileInNewTab(billingData?.roFile);
                      setRoModalOpen(false);
                    }}
                  >
                    View File
                  </Button>

                  <Upload customRequest={handleUploadRo} showUploadList={false}>
                    <Button
                      type="default"
                      icon={<AiOutlineUpload style={{ marginRight: 2 }} />}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                      }}
                      loading={roUploading}
                      disabled={roUploading || !canEditRoFile}
                    >
                      Update RO
                    </Button>
                  </Upload>
                </div>
              )}
            </div>
          </Modal>
        </div>
      </div>
      )}

      {billingData?.splitAmount && (
        <div className="sales-order-field">
          <div className="sales-order-field-name">Total Splitted Amount</div>
          <div className="sales-order-input-parent">
            <Input
              className="custom-input-bdf"
              value={formatIndianNumber(billingData?.splitAmount)}
              readOnly
            />
          </div>
        </div>
      )}

      <div className="sales-order-field">
        <div className="sales-order-field-name">Invoice Remark</div>
        <div className="sales-order-input-parent">
          <Input.TextArea
            className="custom-input-bdf"
            rows={3}
            value={billingData?.invoiceRemark ?? DEFAULT_INVOICE_REMARK}
            onChange={(e) =>
              handleFormChange("invoiceRemark", e.target.value)
            }
            readOnly={justView}
            placeholder={DEFAULT_INVOICE_REMARK}
          />
        </div>
      </div>

      <div className="sales-order-field">
        <div className="sales-order-field-name">Sub Remark</div>
        <div className="sales-order-input-parent">
          <Input.TextArea
            className="custom-input-bdf"
            rows={2}
            value={billingData?.invoiceSubRemark ?? ""}
            onChange={(e) =>
              handleFormChange("invoiceSubRemark", e.target.value)
            }
            readOnly={justView}
            placeholder="Optional — shown below invoice remark on PDF when filled"
          />
        </div>
      </div>

      {/* {isArchivedbillingInvoices && isArchivedbillingInvoices.length > 0 && (
        <div className="sales-order-field">
          <div className="sales-order-field-name">
            Cancelled Billing Invoices
          </div>
          <div className="sales-order-input-parent">
            <Select
              className="custom-select-bdf"
              placeholder="Select Billing Invoice"
              onChange={(value) => fetchArchivedInvoiceDataByID(value)}
              disabled={justView}
            >
              {isArchivedbillingInvoices.map((invoice) => (
                <Option key={invoice?._id} value={invoice?._id}>
                  {invoice?.billNumber}
                </Option>
              ))}
            </Select>
          </div>
        </div>
      )} */}
    </div>
  );
};

export default BillingForm;
